/**
 * **端口连接态同步** + 变参行压紧 —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 视图状态不该手工维护：`row.socket.connected` / `row.connected` /
 * `row.labels` / `row.instanceTypes` 全部**由边推导**，边一变就重算。
 *
 * 顺带做一件容易被忽略的事 —— **变参行压紧**：
 * 实例用序号寻址（`args.0` / `args.1`…），删掉中间一个之后，
 * 后面所有实例的序号都要前移，它们的连线也得跟着改 `targetHandle`。
 * 不然线会指向不存在（或错位）的端口。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * 依赖由宿主通过 `ctx` 注入。搬的时候**一行逻辑都没改** ——
 * 一次几千行的机械改名才是最容易出事的地方。
 */
import { watchEffect } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge } from '@/graph/flowTypes'
import {
  compactIndexMap,
  isOutputRepeater,
  repeaterCountFor,
  repeaterEdges,
  repeaterHandle,
  repeaterIndexOf,
  repeaterSide,
} from '@/node/rows'
import type { GraphNodeModel, RepeaterRow } from '@/types/node'
import type { SocketType } from '@/types/socket'
import type { EdgeView, NodeView } from './useSubgraphLevels'

export interface ProjectionCtx {
  nodes: { value: unknown }
  edges: { value: Edge[] }
  nodeViews: () => NodeView[]
  edgeViews: () => EdgeView[]
  modelOf: (id: string) => GraphNodeModel | undefined
  /** 某条边的端口类型（按实例确认类型用） */
  socketTypeOf: (nodeId?: string | null, handleId?: string | null) => SocketType | undefined
  /** 正在拖线：拖拽中不压紧（否则手底下那一行会中途变意义） */
  linking: { value: boolean }
  /** 正在拖节点：拖拽中跳过（拖节点不会改连线，没必要跑） */
  draggingNodes: { value: boolean }
}

export function useProjection(ctx: ProjectionCtx) {
  const {
    nodes,
    edges,
    nodeViews,
    edgeViews,
    modelOf,
    socketTypeOf,
    linking,
    draggingNodes,
  } = ctx

/** 待压紧的变参行：rowId → (旧序号 → 新序号) */
interface Compaction {
  nodeId: string
  row: RepeaterRow
  map: Map<number, number>
}

/**
 * 上游那个端口的显示名：`节点名 · 行名`。
 *
 * 用来给变参实例做自动标签（见下面 watchEffect 里那段）。
 */
function upstreamLabel(edge: { source: string; sourceHandle?: string | null }): string | undefined {
  const model = modelOf(edge.source)
  if (!model) return undefined
  const row = model.rows.find(
    (r) => (r.kind === 'input' || r.kind === 'output') && r.socket.id === edge.sourceHandle,
  )
  const rowLabel =
    row && (row.kind === 'input' || row.kind === 'output') ? row.label : undefined
  const nodeLabel = model.label || model.title
  return rowLabel ? `${nodeLabel} · ${rowLabel}` : nodeLabel
}

watchEffect(() => {
  /*
   * 拖拽中直接跳过：拖节点不会改连线，投影没必要跑。
   * 提前 return 只会少跟踪依赖 —— 拖拽结束时 draggingNodes 变化会唤醒它，
   * 那时再完整读一遍。
   */
  if (draggingNodes.value) return

  const list = edgeViews()
  const compactions: Compaction[] = []
  for (const node of nodeViews()) {
    const model = node.data?.model
    if (!model) continue
    for (const row of model.rows) {
      if (row.kind === 'input') {
        row.socket.connected = list.some(
          (e) => e.target === node.id && e.targetHandle === row.socket.id,
        )
      } else if (row.kind === 'output') {
        row.socket.connected = list.some(
          (e) => e.source === node.id && e.sourceHandle === row.socket.id,
        )
      } else if (row.kind === 'repeater') {
        /*
         * 由边推导：哪些实例有连线、最后一个已连实例的序号。
         * 复用 repeaterCountFor，保证与渲染端的 min/max 钳制完全一致。
         *
         * ⚠️ **按方向取边**：输入变参行是这个节点的 `target`，
         * 输出变参行是它的 `source` —— 方向的分界线只有
         * `repeaterEdges` 一处实现（见 `node/rows.ts`）。
         */
        const links = repeaterEdges(row, node.id, list)
        const connectedIndexes = new Set<number>()
        let lastConnected = -1
        for (const link of links) {
          connectedIndexes.add(link.index)
          if (link.index > lastConnected) lastConnected = link.index
        }
        const outRow = isOutputRepeater(row)

        const total = repeaterCountFor(row, lastConnected)
        const next: boolean[] = []
        for (let i = 0; i < total; i += 1) next.push(connectedIndexes.has(i))

        // 只有真的变了才赋值，避免每次 watchEffect 都触发下游重渲染
        const prev = row.connected ?? []
        const changed =
          prev.length !== next.length || next.some((v, i) => v !== prev[i])
        if (changed) row.connected = next

        /*
         * 顺带算**自动标签**：接了线就用上游那个端口的名字。
         *
         * 「监听 0 / 监听 1」在接上线之后就没意义了 —— 用户想看的是
         * 「组件 · 文本 · 内容」。没接的实例仍然回退到占位名。
         */
        const labels: (string | undefined)[] = []
        for (let i = 0; i < total; i += 1) {
          const link = links.find((l) => l.index === i)
          /*
           * ⚠️ 自动标签是「跟着**上游**那个端口的名字」—— 只对输入方向成立。
           * 输出方向没有上游（值从这里出去），硬套会得到下游的名字，
           * 那是另一个概念（下游叫什么和这一路输出是什么无关）。
           */
          labels.push(link && !outRow ? upstreamLabel(link.edge) : undefined)
        }
        /*
         * 顺带**确认每个实例的类型**：连上了就取上游那个端口的类型，
         * 没连就保持 `any`（通配，什么都能接）。
         *
         * 这是「组的输入未链接是 any、链接后确认类型」那条要求的落点 ——
         * 变参行只有一个 socketType，做不到按路区分，所以按实例记。
         */
        const types: (SocketType | undefined)[] = []
        for (let i = 0; i < total; i += 1) {
          const link = links.find((l) => l.index === i)
          /*
           * ★★ 输出方向的实例类型**不由下游推导** —— 那是节点**声明**的
           * （和普通输出行一样）。让下游改它的话，一个 `text` 输出接到
           * `any` 输入上就会把自己的类型改掉，属于本末倒置。
           * 所以这里只对输入方向确认类型。
           */
          types.push(
            !outRow && link
              ? socketTypeOf(link.edge.source, link.edge.sourceHandle)
              : undefined,
          )
        }
        const prevTypes = row.instanceTypes ?? []
        const typesChanged =
          prevTypes.length !== types.length ||
          types.some((v, i) => v !== prevTypes[i])
        if (typesChanged) row.instanceTypes = types

        const prevLabels = row.labels ?? []
        const labelsChanged =
          prevLabels.length !== labels.length ||
          labels.some((v, i) => v !== prevLabels[i])
        if (labelsChanged) row.labels = labels

        /*
         * 顺带检查有没有「空洞」—— 已连接的实例必须占据 0..k-1。
         *
         * 注意这里只是**收集**，真正的写边放在下面：
         * 在遍历里写边会让「读 edges → 写 edges」自激。
         */
        const occupied = [...connectedIndexes].sort((a, b) => a - b)
        if (occupied.some((oldIndex, i) => oldIndex !== i)) {
          compactions.push({ nodeId: node.id, row, map: compactIndexMap(occupied) })
        }
      }
    }
  }

  /*
   * 拖拽过程中**不压紧**。
   *
   * 否则「把 args.0 的线拽下来」的瞬间，args.1 的线就会被顶到 args.0 上 ——
   * 用户手底下那一行会在拖拽中途变意义。压紧推迟到松手之后
   * （onConnectEnd 会清掉 linkingType，这个 effect 随即重跑并补上）。
   */
  if (compactions.length === 0 || linking.value) return

  type LooseEdge = {
    source: string
    target: string
    sourceHandle?: string | null
    targetHandle?: string | null
    [key: string]: unknown
  }

  let changed = false
  const rebuilt = (edges.value as unknown as LooseEdge[]).map((edge) => {
    for (const { nodeId, row, map } of compactions) {
      /* ⚠️ 同样要分方向：输入变参行的边挂在 target，输出挂在 source */
      const side = repeaterSide(row)
      const owner = side === 'input' ? edge.target : edge.source
      if (owner !== nodeId) continue
      const handleId = side === 'input' ? edge.targetHandle : edge.sourceHandle
      const at = repeaterIndexOf(row, handleId ?? '')
      if (at < 0) continue
      const to = map.get(at)
      if (to === undefined || to === at) continue
      changed = true
      return side === 'input'
        ? { ...edge, targetHandle: repeaterHandle(row, to) }
        : { ...edge, sourceHandle: repeaterHandle(row, to) }
    }
    return edge
  })

  // 只写真正变过的，否则会无限重跑
  if (changed) edges.value = rebuilt as unknown as Edge[]
})
}
